import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { auditService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Card, Badge, Input, EmptyState, Spinner, Pagination } from '../../components/ui';

const ACTION_TONE = {
  approve: 'green',
  suspend: 'red',
  reject: 'red',
  delete: 'red',
  resolve: 'blue',
};

function toneForAction(action) {
  const suffix = action.split('.').pop().replace('bulk_', '');
  return ACTION_TONE[suffix] || 'gray';
}

function AdminAuditLog() {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [ordering, setOrdering] = useState('-created_at');
  const pageSize = 20;

  const fetchEntries = useCallback(() => {
    setLoading(true);
    const params = { page, ordering };
    if (search) params.action = search;
    auditService.getEntries(params)
      .then((res) => {
        const data = res.data;
        setEntries(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load audit log'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, ordering]);

  useEffect(() => {
    const id = setTimeout(fetchEntries, 300);
    return () => clearTimeout(id);
  }, [fetchEntries]);

  useEffect(() => { setPage(1); }, [search, ordering]);

  const toggleDurationSort = () => {
    setOrdering((current) => current === '-duration_ms' ? 'duration_ms' : current === 'duration_ms' ? '-created_at' : '-duration_ms');
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Audit Log')} subtitle={t('A record of admin and moderation actions')} />
        <main className="flex-1 overflow-y-auto p-6">
          <Card padded={false} className="overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <Input
                placeholder={t('Filter by action (e.g. teacher.approve)...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="sm:max-w-sm"
              />
            </div>

            {loading ? (
              <div className="p-12 flex justify-center"><Spinner /></div>
            ) : entries.length === 0 ? (
              <EmptyState title={t('No audit entries found')} description={t('Admin and moderation actions will appear here.')} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">{t('When')}</th>
                      <th className="text-left px-4 py-3">{t('Actor')}</th>
                      <th className="text-left px-4 py-3">{t('Action')}</th>
                      <th className="text-left px-4 py-3">{t('Target')}</th>
                      <th className="text-right px-4 py-3" aria-sort={ordering === '-duration_ms' ? 'descending' : ordering === 'duration_ms' ? 'ascending' : 'none'}>
                        <button type="button" onClick={toggleDurationSort} className="inline-flex items-center gap-1 font-semibold hover:text-primary-700" title={t('Sort by duration')}>
                          {t('Duration')}
                          <span aria-hidden="true" className={ordering.includes('duration_ms') ? 'text-primary-600' : 'text-gray-300'}>{ordering === '-duration_ms' ? '↓' : ordering === 'duration_ms' ? '↑' : '↕'}</span>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {entries.map((e) => (
                      <tr key={e.id} className="hover:bg-gray-50 align-top">
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {new Date(e.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {e.actor ? `${e.actor.first_name} ${e.actor.last_name}` : t('System')}
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={toneForAction(e.action)}>{e.action}</Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {e.target_repr}
                          {e.metadata && Object.keys(e.metadata).length > 0 && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {Object.entries(e.metadata).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap tabular-nums">
                          {e.duration_ms === null || e.duration_ms === undefined ? '—' : `${Number(e.duration_ms).toFixed(2)} ms`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Pagination page={page} count={count} pageSize={pageSize} onPageChange={setPage} />
          </Card>
        </main>
      </div>
    </div>
  );
}

export default AdminAuditLog;
